import React, { useEffect, useState } from "react";

import { yupResolver } from "@hookform/resolvers/yup";
import {
  Button,
  Divider,
  Form,
  Grid,
  Input,
  Section,
} from "@samskip/frontend-components";
import { navigate } from "gatsby";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { nameof } from "ts-simple-nameof";

import {
  AddressType,
  CommonConstants,
  BookingTemplateConstants,
  NavigationConstants,
} from "@assets/constants";
import { BookingTemplateFormTypes } from "@assets/formTypes";
import {
  defaultRouteForm,
  RouteForm,
  transferAddressFormFieldNames,
  TransferAddressSectionFields,
  TransportRequestForm,
} from "@assets/formTypes/bookingTemplates";
import { useAppSelector } from "@assets/hooks";
import { BookingTemplateMappings } from "@assets/mappings";
import { CommonTypes, Company, RegistryTypes, PortTypes } from "@assets/types";
import {
  getErrorMessage,
  IncotermUtilities,
  WindowUtilities,
} from "@assets/utilities";
import { swapFieldValues } from "@assets/utilities/formUtilities";
import { getIncotermPorts } from "@assets/utilities/incotermUtilities";
import { BookingTemplatesYup } from "@assets/yupObjects";
import {
  AddressesSection,
  IncotermsSection,
  TransferAddressesSection,
  LeavePageModal,
} from "@components";
import { incotermsState } from "@state/slices/registrySlice";
import { userProfileState } from "@state/slices/userProfileSlice";

interface BookingTemplatesRouteFormProps {
  /** The values of the form. */
  formValues: BookingTemplateFormTypes.RouteForm;
  /** When clicking on the next or submit button. */
  onNextClick: CommonTypes.OneParamCallbackFunction<BookingTemplateFormTypes.RouteForm>;
  /**When clicking the Discard/Cancel button */
  onCancelClick: CommonTypes.CallbackFunction;
  /** The page mode */
  pageMode?: NavigationConstants.PAGE_MODE;
  /** The id of the template that's being updated. */
  updateTemplateId?: number;
}

/**
 * Returns the form values based on the selected company and whether it is the shipper or the consignee.
 * @param userSelectedCompany The selected company by the user.
 * @param bookingParty What booking party is the selected company.
 * @param routeValues The route form values
 * @returns The updated routeValues with the user's selected company either as Consignee, either as Shipper
 */
const getFormValuesWithSelectedCompany = async (
  userSelectedCompany: Company,
  bookingPartyType: AddressType,
  routeValues: BookingTemplateFormTypes.RouteForm
) => {
  const userCompanyPartner = await BookingTemplateMappings.companyToPartnerForm(
    userSelectedCompany
  );
  if (bookingPartyType === AddressType.SHP) {
    return {
      ...routeValues,
      Shipper: userCompanyPartner,
      BookingParty: userCompanyPartner.PartnerCompany?.PartnerCode,
    };
  }
  return {
    ...routeValues,
    Consignee: userCompanyPartner,
    BookingParty: userCompanyPartner.PartnerCompany?.PartnerCode,
  };
};

const BookingTemplatesRoute = ({
  formValues,
  onCancelClick,
  onNextClick,
  pageMode = NavigationConstants.PAGE_MODE.CREATE,
  updateTemplateId,
}: BookingTemplatesRouteFormProps) => {
  const { t } = useTranslation();
  const userProfile = useAppSelector(userProfileState).userProfile;
  const [incotermPoints, setIncotermPoints] = useState<PortTypes.Port[]>([]);
  const [filteredIncoterms, setFilteredIncoterms] = useState<
    RegistryTypes.Incoterm[]
  >([]);

  const incoterms = useAppSelector(incotermsState);

  const {
    control,
    formState: { errors },
    getValues,
    handleSubmit,
    register,
    setValue,
    reset,
  } = useForm({
    defaultValues: formValues,
    resolver: yupResolver(BookingTemplatesYup.routeValidationSchema),
    mode: "onTouched",
  });

  useEffect(() => {
    WindowUtilities.scrollToTop();
    // Edit the form values  only if the userProfile is loaded and there are no values for the selected address type
    if (
      userProfile &&
      ((formValues.BookingPartyType === AddressType.SHP &&
        !formValues.Shipper) ||
        (formValues.BookingPartyType === AddressType.CEE &&
          !formValues.Consignee))
    ) {
      getFormValuesWithSelectedCompany(
        userProfile?.SelectedCompany,
        formValues.BookingPartyType,
        formValues
      ).then((newFormValues) => {
        reset(newFormValues);
      });
    }
    filterIncoterms();
  }, []);

  //  --- Shipper, consignee, notifier section
  const swapPartners = () => {
    swapFieldValues("Shipper", "Consignee", getValues, setValue);
  };

  const onAddressSave = (
    id: BookingTemplateConstants.PARTNER_ADDRESS_TYPE,
    data: BookingTemplateFormTypes.PartnerForm
  ) => {
    setValue(id, data);
  };

  const onAddressRemove = (
    id: BookingTemplateConstants.PARTNER_ADDRESS_TYPE
  ) => {
    setValue(id, defaultRouteForm[id]);
  };

  //  --- To, From section
  const resetPlaceFields = (placesFieldNames: TransferAddressSectionFields) => {
    const fieldsToReset = [
      nameof<TransferAddressSectionFields>((item) => item.Country),
      nameof<TransferAddressSectionFields>((item) => item.Point),
      nameof<TransferAddressSectionFields>((item) => item.Port),
    ];

    fieldsToReset.forEach((fieldName) =>
      setValue(
        placesFieldNames[fieldName],
        defaultRouteForm[placesFieldNames[fieldName]]
      )
    );
    setValue("Incoterm", defaultRouteForm.Incoterm);
    setValue("IncotermPoint", defaultRouteForm.IncotermPoint);
  };

  const resetOnPlaceChange = (
    addressSection: BookingTemplateConstants.TRANSFER_ADDRESS_TYPE
  ) => {
    resetPlaceFields(transferAddressFormFieldNames[addressSection]);
  };

  const handleResetCity = (
    transferAddressType: BookingTemplateConstants.TRANSFER_ADDRESS_TYPE
  ) => {
    setValue(
      transferAddressFormFieldNames[transferAddressType].Point,
      defaultRouteForm[transferAddressFormFieldNames[transferAddressType].Point]
    );
    filterIncoterms();
  };

  const handleSetTransportRequest = (
    transportRequests: TransportRequestForm[],
    transferAddressType: BookingTemplateConstants.TRANSFER_ADDRESS_TYPE
  ) => {
    setValue(
      transferAddressFormFieldNames[transferAddressType].TransportRequest,
      transportRequests
    );
  };

  const handleSetTransportRequestReset = (
    transferAddressType: BookingTemplateConstants.TRANSFER_ADDRESS_TYPE
  ) => {
    setValue(
      transferAddressFormFieldNames[transferAddressType].TransportRequest,
      defaultRouteForm[
        transferAddressFormFieldNames[transferAddressType].TransportRequest
      ]
    );
  };
  // --- Incoterm section
  const updateIncotermPlaces = () => {
    const incotermPlaces = getIncotermPorts(getValues("Incoterm"), {
      PLR: getValues(CommonConstants.INCOTERM_POINT.PLR),
      POL: getValues(CommonConstants.INCOTERM_POINT.POL),
      PFD: getValues(CommonConstants.INCOTERM_POINT.PFD),
      POD: getValues(CommonConstants.INCOTERM_POINT.POD),
    });
    setIncotermPoints(incotermPlaces);

    if (
      incotermPlaces.filter(
        (place) => place.PointCode === getValues("IncotermPoint")?.PointCode
      ).length === 0
    ) {
      setValue(
        "IncotermPoint",
        incotermPlaces[0] ?? defaultRouteForm.IncotermPoint
      );
    }
  };

  const filterIncoterms = () => {
    const { filteredIncoterms: filteredTerms, wasSelectedRemoved } =
      IncotermUtilities.getFilteredIncoterms(
        incoterms,
        {
          PLR: getValues("PLR"),
          POL: getValues("POL"),
          PFD: getValues("PFD"),
          POD: getValues("POD"),
        },
        getValues("Incoterm")?.TermsCode ?? null
      );
    if (wasSelectedRemoved) {
      setValue("Incoterm", defaultRouteForm.Incoterm);
    }
    updateIncotermPlaces();
    setFilteredIncoterms(filteredTerms);
  };

  // --- General

  const handleNextClick = (data: BookingTemplateFormTypes.RouteForm) => {
    onNextClick(data);
  };

  return (
    <Form id="booking_templates_form">
      <Section>
        <p className="text-neutral-800 text-sm">{t("TEXT_TEMPLATE_DETAILS")}</p>
        <Grid mdCol={3}>
          <Input
            errorMessage={getErrorMessage(
              nameof<RouteForm>((i) => i.BookingTemplateName),
              errors
            )}
            fullWidth
            hasError={
              errors[nameof<RouteForm>((i) => i.BookingTemplateName)] != null
            }
            id="template-name"
            label={t("LABEL_TEMPLATE_NAME")}
            maxLength={BookingTemplateConstants.MAX_INPUT_TEMPLATE_NAME}
            required
            {...register("BookingTemplateName")}
            placeholder={t("PLACEHOLDER_TEMPLATE_NAME")}
          />
          {pageMode === NavigationConstants.PAGE_MODE.UPDATE && (
            <Input
              id="template-id"
              label={t("LABEL_TEMPLATE_ID")}
              readOnly
              value={updateTemplateId?.toString()}
            />
          )}
        </Grid>
      </Section>
      <Divider />
      <AddressesSection
        control={control}
        onAddressRemove={onAddressRemove}
        onAddressSave={onAddressSave}
        onAddressTypeChange={swapPartners}
        register={register}
      />
      <Grid mdCol={3}>
        <TransferAddressesSection
          control={control}
          errors={errors}
          onCityChange={filterIncoterms}
          onCountryChange={handleResetCity}
          onPlaceChange={resetOnPlaceChange}
          onPortChange={updateIncotermPlaces}
          onTransportRequestChange={handleSetTransportRequest}
          onTransportRequestClear={handleSetTransportRequestReset}
          register={register}
          type={BookingTemplateConstants.TRANSFER_ADDRESS_TYPE.FROM}
        />
        <TransferAddressesSection
          control={control}
          errors={errors}
          onCityChange={filterIncoterms}
          onCountryChange={handleResetCity}
          onPlaceChange={resetOnPlaceChange}
          onPortChange={updateIncotermPlaces}
          onTransportRequestChange={handleSetTransportRequest}
          onTransportRequestClear={handleSetTransportRequestReset}
          register={register}
          type={BookingTemplateConstants.TRANSFER_ADDRESS_TYPE.TO}
        />
      </Grid>
      <Divider />
      <IncotermsSection
        control={control}
        errors={errors}
        incotermPoints={incotermPoints}
        incoterms={filteredIncoterms}
        updateIncotermPlaces={updateIncotermPlaces}
      />
      <Divider />
      <div className="flex flex-row justify-between items-center">
        <LeavePageModal
          //Navigate to templates list page
          isLink
          onLeave={() => {
            onCancelClick();
            navigate(
              `${NavigationConstants.NAVIGATION_LINKS.bookingTemplates}`
            );
          }}
        />

        <Button onClick={handleSubmit(handleNextClick)} variant="primary">
          {t("LABEL_CONTINUE")}
        </Button>
      </div>
    </Form>
  );
};

export default BookingTemplatesRoute;
